import axios from "axios";
import { baseUri } from "src/config.ts";
import { msalInstance } from "src/lib/msal";
import { CustomerPolygon, Parcel } from "src/types";
import { CustomerAccountTeam } from "src/types/customer-account-team-management/customer-account-team";

type CustomerLocation = {
    CustomerCode: string;
    CustomerName: string;
    ShippingAddress_Street1: string;
    ShippingAddress_Street2: string;
    ShippingAddress_City: string;
    ShippingAddress_State: string;
    ShippingAddress_Zip: string;
    ShippingAddress_Zip4: string;
    ShippingAddress_Country: string;
    Latitude: number;
    Longitude: number;
};

type CustomerLocationResponse = {
    Data: CustomerLocation;
    Message: null | string;
    Status: "ok";
};

const customerServiceApiInstance = axios.create({
    baseURL: baseUri,
});

customerServiceApiInstance.interceptors.request.use(
    async (config) => {
        const account = msalInstance.getAllAccounts()[0];
        if (account) {
            const { accessToken } = await msalInstance.acquireTokenSilent({
                scopes: ["User.Read"],
                account: account,
            });

            config.headers.Authorization = `Bearer ${accessToken}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    },
);

export async function getShippingInfo(customerCode: string) {
    const response =
        await customerServiceApiInstance.get<CustomerLocationResponse>(
            `/${customerCode}/shipping_info`,
        );

    if (response.data.Status !== "ok") {
        throw new Error(
            response.data.Message ??
                "An error occurred while fetching shipping info",
        );
    }

    return response.data.Data;
}

export async function getCustomerHistory(customerCode: string) {
    const response = await customerServiceApiInstance.get(
        `/history/${customerCode}`,
    );
    return response.data.Data;
}

export async function updateShippingInfo(
    customerCode: string,
    shippingInfo: {
        UpdateByUser: string;
        ShippingAddress1: string;
        ShippingAddress2: string;
        ShippingCity: string;
        ShippingState: string;
        ShippingZip: string;
        ShippingZip2: string;
        ShippingCountry: string;
        AreaCode: number;
        Phone: number;
        Latitude: number;
        Longitude: number;
    },
) {
    const response =
        await customerServiceApiInstance.put<CustomerLocationResponse>(
            `/${customerCode}/shipping_info`,
            shippingInfo,
        );
    return response.data.Data;
}

export async function getCustomerAccountTeams() {
    const response = await customerServiceApiInstance.get(
        `/customer-account-team`,
    );
    return response.data.Data as CustomerAccountTeam[];
}

export async function getCustomerAccountTeam(teamID: string) {
    const response = await customerServiceApiInstance.get(
        `/customer-account-team/${teamID}`,
    );
    return response.data.Data;
}

//Account
export async function addMember(
    teamID: string,
    { employeeCode, role }: { employeeCode: string; role: string },
) {
    await customerServiceApiInstance.post(`/customer-account-team/${teamID}`, {
        EmployeeCode: employeeCode,
        Role: role,
    });
}

export async function addTeam({
    teamName,
    teamEmail,
    teamCode,
    teamManager,
}: {
    teamName: string;
    teamEmail: string;
    teamCode: string;
    teamManager: string;
}) {
    await customerServiceApiInstance.post(`/customer-account-team`, {
        TeamName: teamName,
        TeamEmail: teamEmail,
        TeamCode: teamCode,
        TeamManager: teamManager,
    });
}

export async function saveMember(
    teamId: string,
    member: string,
    { role }: { role: string },
) {
    await customerServiceApiInstance.put(
        `/customer-account-team/${teamId}/member/${member}`,
        {
            Role: role,
        },
    );
}

export async function deleteMember({
    teamId,
    member,
}: {
    teamId: string;
    member: string;
}) {
    await customerServiceApiInstance.delete(
        `/customer-account-team/${teamId}/member/${member}`,
    );
}

type CustomerPolygonApiResponse = {
    Data: CustomerPolygon;
    Message: string | null;
    Status: "ok" | "error";
};

export async function fetchCustomerPolygon({
    customerCode,
}: {
    customerCode: string;
}) {
    const { data } =
        await customerServiceApiInstance.get<CustomerPolygonApiResponse>(
            `/${customerCode}/polygon`,
        );

    if (data.Status === "error") {
        throw new Error(
            data.Message ?? "An error occurred while fetching customer polygon",
        );
    }

    return data.Data;
}

export async function putCustomerPolygon({
    customerCode,
    employeeCode,
    parcelGeoJson,
}: {
    customerCode: string;
    parcelGeoJson: string;
    employeeCode: string;
}) {
    await customerServiceApiInstance.put(`/${customerCode.trim()}/polygon`, {
        GeoJson: parcelGeoJson,
        UserCode: employeeCode,
    });
}

type FetchParcelApiResponse = {
    Data: Parcel | null;
    Message: string | null;
    Status: "ok" | "error";
};

export async function fetchParcel({
    latitude,
    longitude,
    address,
}: {
    latitude?: number;
    longitude?: number;
    address?: string;
}) {
    // Weird. It's a POST.
    const { data } =
        await customerServiceApiInstance.post<FetchParcelApiResponse>(
            `/parcel`,
            {
                Latitude: latitude,
                Longitude: longitude,
                Address: address,
            },
        );

    if (data.Status === "error") {
        throw new Error(
            data.Message ?? "An error occurred while fetching parcel",
        );
    }

    return data.Data;
}
